'use client'

import useActiveItemOffer from '@/utils/hooks/useActiveItemOffer'
import CancelOfferModal from '@/components/offers/cancel-offer-modal'
import type { Offer as CancellableOffer } from '@/utils/offers'
import React, { FC } from 'react'
import styles from './InscriptionActionRow.module.css'
import BitcoinIcon from '@/icons/bitcoin-filled'
import useInscriptionOutpoint from '@/utils/hooks/useInscriptionOutpoint'
import useInscription from '@/utils/hooks/useInscription'
import useInscriptionListing, { fetchInscriptionListing } from '@/utils/hooks/useInscriptionListing'
import { isSnipeProtectedListing } from '@/utils/market/secure-purchase'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import usePurchaseModal from '@/utils/hooks/usePurchaseModal'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useSatoshiPriceUSD from '@/utils/hooks/useSatoshiPriceUSD'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import CollectionDetail from '@/components/search-input/pages/collection-detail'
import ItemMarketActions from '@/components/offers/item-market-actions'
import ItemOfferModal from '@/components/offers/item-offer-modal'
import ItemOffersPanel from '@/components/offers/item-offers-panel'
import ReviewOfferModal from '@/components/offers/review-offer-modal'
import { offersEnabledFor, type Offer } from '@/utils/offers'
import useIsHydrated from '@/utils/hooks/useIsHydrated'
import useCollection from '@/utils/hooks/useCollection'
import SecureListingBadge from '@/components/secure-listing-badge'

const InscriptionActionRow: FC<{ inscription: any }> = ({ inscription: initial }) => {
	const { data: outpointData, mutate } = useInscriptionOutpoint(initial.id)
	const { data: live } = useInscription(initial.id)
	const {
		data: escrow,
		error,
		mutate: mutateListing,
		isValidating: listingValidating
	} = useInscriptionListing(initial.id)
	const loggedIn = useLoggedIn()
	const inscription = live || initial
	const hydrated = useIsHydrated()
	const showOffers = hydrated && offersEnabledFor(inscription?.collection?.slug, inscription)
	const [offerOpen, setOfferOpen] = React.useState(false)
	const activeItemOffer = useActiveItemOffer(initial.id, showOffers)
	const [cancellingOffer, setCancellingOffer] = React.useState<CancellableOffer | null>(null)
	const openOfferAction = () => activeItemOffer ? setCancellingOffer(activeItemOffer) : setOfferOpen(true)
	const [reviewOffer, setReviewOffer] = React.useState<{
		offer: Offer
		action: 'accept' | 'decline'
	} | null>(null)
	const { data: collection } = useCollection(inscription?.collection?.slug)
	const [transferredId, setTransferredId] = React.useState<string>()
	const [completedId, setCompletedId] = React.useState<string>()
	const [checking, setChecking] = React.useState(false)
	const clickLock = React.useRef(false)
	const listingReady = escrow !== undefined && !error
	const forSale = Number(escrow?.satoshi_price) > 0 && completedId !== initial.id
	const isOwner = Boolean(
		loggedIn &&
			transferredId !== initial.id &&
			(escrow?.seller_address || outpointData?.owner) === Wallet.getTaprootAddress()
	)
	const inscriptionWithEscrow = React.useMemo(
		() => ({ ...inscription, escrow }),
		[inscription, escrow]
	)
	const [, openPurchase] = usePurchaseModal(inscriptionWithEscrow, { autoSign: true })
	const price = useSatoshiPrice(escrow?.satoshi_price || 0)
	const [priceUSD, loadingPriceUSD] = useSatoshiPriceUSD(escrow?.satoshi_price || 0)

	React.useEffect(() => {
		const onPurchase = (payload: { ids?: string[] }) => {
			if (!payload.ids?.includes(initial.id)) return
			setCompletedId(initial.id)
			void mutate()
			void mutateListing()
		}
		event.listen('purchase-completed', onPurchase)
		return () => event.removeListener('purchase-completed', onPurchase)
	}, [initial.id, mutate, mutateListing])

	React.useEffect(() => {
		const onTransfer = (payload: { event?: string; inscription?: unknown }) => {
			if (payload.event !== 'success' || payload.inscription != null) return
			setTransferredId(initial.id)
			void mutate()
		}
		event.listen('open-broadcast-modal', onTransfer)
		return () => event.removeListener('open-broadcast-modal', onTransfer)
	}, [initial.id, mutate])

	const handleCollectionAction = (tab: string) => {
		event.emit('command', {
			command: {
				featured: false,
				component: CollectionDetail,
				name: inscription?.collection?.name || 'Inscription',
				args: { slug: inscription?.collection?.slug, tab },
				heading: 'Collections'
			}
		})
	}

	const handleBuy = async () => {
		if (clickLock.current || !forSale || isOwner) return
		if (!Wallet.isSet()) {
			event.emit('command', { id: 'connect-wallet' })
			return
		}
		clickLock.current = true
		setChecking(true)
		try {
			// The page may have been open while this listing sold or changed price.
			const current = await fetchInscriptionListing(initial.id, true)
			await mutateListing(current, { revalidate: false })
			if (!current) {
				throw new Error('This inscription is no longer listed for sale.')
			}
			if (
				Number(current.satoshi_price) !== Number(escrow.satoshi_price) ||
				current.seller_address !== escrow.seller_address ||
				current.outpoint !== escrow.outpoint ||
				isSnipeProtectedListing(current) !== isSnipeProtectedListing(escrow)
			) {
				throw new Error('This listing changed. Review the updated price and try again.')
			}
			await openPurchase()
		} catch (cause) {
			event.emit('snackbar', {
				variant: 'error',
				title:
					cause instanceof Error ? cause.message : 'Could not check this listing. Please try again.'
			})
		} finally {
			clickLock.current = false
			setChecking(false)
		}
	}

	return (
		<>
			<section className={styles.actionRow} aria-label="Listing" data-testid="inscription-listing">
				{!listingReady ? (
					<div
						className="flex items-center justify-between gap-3 text-sm text-muted-foreground"
						role="status"
					>
						<span>
							{error && !listingValidating
								? error instanceof Error
									? error.message
									: 'Listing status unavailable'
								: 'Checking listing…'}
						</span>
						{error && (
							<button
								type="button"
								className="min-h-11 text-primary disabled:opacity-60"
								disabled={listingValidating}
								onClick={() => void mutateListing().catch(() => undefined)}
							>
								{listingValidating ? 'Retrying…' : 'Retry'}
							</button>
						)}
					</div>
				) : completedId === initial.id ? (
					<p className="text-sm text-muted-foreground" role="status">
						Purchase submitted
					</p>
				) : forSale ? (
					<>
						<div>
							<p className="text-xs text-muted-foreground">Current price</p>
							<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
								<BitcoinIcon className="size-5 shrink-0" />
								<span className="text-xl font-semibold tabular-nums">{price} BTC</span>
								{!loadingPriceUSD && (
									<span className="text-sm text-muted-foreground">${priceUSD}</span>
								)}
								<SecureListingBadge listing={inscriptionWithEscrow} />
							</div>
						</div>
						{isOwner && (
							<ButtonGradientEpic
								className="min-h-11 w-full"
								onClick={() => handleCollectionAction('delist')}
							>
								Delist
							</ButtonGradientEpic>
						)}
					</>
				) : (
					<>
						<p className="text-sm text-muted-foreground">Not listed for sale</p>
						{isOwner && (
							<div className="flex flex-wrap gap-3">
								<ButtonGradientEpic
									className="min-h-11 flex-1"
									onClick={() => handleCollectionAction('list')}
								>
									List for sale
								</ButtonGradientEpic>
								<button
									type="button"
									className="min-h-11 flex-1 rounded-md border border-border px-4 text-sm font-medium hover:bg-accent"
									onClick={() => handleCollectionAction('send')}
								>
									Transfer
								</button>
							</div>
						)}
					</>
				)}
				{!isOwner && completedId !== initial.id && transferredId !== initial.id && (
					<ItemMarketActions
						listed={listingReady && forSale}
						showOffer={showOffers}
						buying={checking}
						onBuy={handleBuy}
						hasOffer={!!activeItemOffer} onOffer={openOfferAction}
					/>
				)}
			</section>
			{showOffers && (
				<ItemOffersPanel
					id={initial.id}
					isOwner={isOwner}
					onReview={(offer, action) => setReviewOffer({ offer, action })}
				/>
			)}
			{reviewOffer && (
				<ReviewOfferModal
					offer={reviewOffer.offer}
					itemId={initial.id}
					action={reviewOffer.action}
					onClose={() => setReviewOffer(null)}
					onChanged={() => {
						void mutate()
						void mutateListing()
					}}
				/>
			)}
			{cancellingOffer && <CancelOfferModal offer={cancellingOffer} onClose={() => setCancellingOffer(null)} />}
			{offerOpen && (
				<ItemOfferModal
					inscription={inscription}
					listedPriceSats={forSale ? Number(escrow.satoshi_price) : listingReady ? null : undefined}
					floorSats={collection?.floor_price}
					onClose={() => setOfferOpen(false)}
				/>
			)}
		</>
	)
}

export default InscriptionActionRow
